import * as React from "react"
import styled from "styled-components"
// Note that using the normal Link here from the following:
// import { Link } from "react-router-dom"
// doesn't make anchor links works, see:
// https://github.com/remix-run/react-router/issues/394#issuecomment-220221604
import { HashLink as Link } from "react-router-hash-link"
import {
  ColorVariant,
  ButtonSize,
  getVariantTextColor,
  isColorfulVariant,
  getVariantColor,
  getButtonSizePadding,
  getVariantHoverColor,
  getVariantBorderColor,
} from "../../../utils/theme-helpers"
import * as Aria from "react-aria-components"
import H3 from "../typography/H3"

interface ButtonProps extends ButtonStyleProps {
  onPress?: () => void
  title?: string
  type?: "button" | "submit" | "reset"
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  disabled?: boolean
  link?: string
}

interface ButtonStyleProps {
  fullWidth?: boolean
  variant?: ColorVariant
  size?: ButtonSize
  noText?: boolean
}

const ButtonStyle = styled(Aria.Button)<ButtonStyleProps>`
  border: none;
  background: none;
  outline: none;
  display: block;

  height: fit-content;

  background: ${({ size, variant, theme }) =>
    size === "big" && isColorfulVariant(variant) ? theme.colors.whiteTransparent : "none"};
  border-radius: ${({ theme }) => theme.borders.buttonRadius};
  box-shadow: 0px 1px 3px 1px rgba(119, 119, 119, 0.25);

  color: ${({ variant, size }) => getVariantTextColor(variant, size)};

  &:hover {
    color: ${({ variant, size }) => getVariantTextColor(variant, size)};
  }

  padding: ${({ size, noText }) => getButtonSizePadding(size, noText)};
  width: ${(props) => (props.fullWidth ? "100%" : "auto")};

  cursor: pointer;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    inset: 0;

    border: ${({ theme, variant }) =>
      `${!isColorfulVariant(variant) ? theme.borders.smallWidth : "0"} solid ${getVariantBorderColor(variant)}`};
    border-radius: ${({ theme }) => theme.borders.buttonRadius};

    background: ${({ variant }) => getVariantColor(variant)};
    background-size: 200% 100%;
    background-position: 100% 0%;

    transition-property: opacity ${({ variant }) => (isColorfulVariant(variant) ? "background-position" : "background")};
    transition-duration: 0.3s;
    transition-timing-function: ease-out;

    opacity: ${({ size, variant }) => (size === "big" && isColorfulVariant(variant) ? 0 : 1)};
  }

  &:disabled {
    cursor: default;
  }

  &:disabled::before {
    background: ${({ theme }) => theme.colors.greyDisabled};
    color: ${({ theme }) => theme.colors.white};
  }

  &:active:not(:disabled) {
    box-shadow: none;
  }

  &:hover:not(:disabled)::before {
    background: ${({ variant }) => getVariantHoverColor(variant)};
    background-size: 200% 100%;
    background-position: ${({ variant, size }) => (isColorfulVariant(variant) && size !== "big" ? "0%" : "100%")} 0%;
    opacity: 1;
  }
`

const ButtonContentStyle = styled.span<{ size: ButtonSize }>`
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: ${({ size }) => (size === "small" ? "4px" : "8px")};
  z-index: 4;
  position: relative;
`

/**
 * A button.
 */
const Button: React.FunctionComponent<ButtonProps> = ({
  iconLeft,
  iconRight,
  title,
  variant = "dark",
  size = "normal",
  link,
  disabled,
  ...props
}) => {
  const noText = !title || title?.length == 0
  return (
    <ButtonStyle
      {...props}
      noText={noText}
      isDisabled={disabled}
      variant={variant}
      size={size}
      as={link ? Link : Aria.Button}
      to={link}
    >
      <ButtonContentStyle size={size}>
        {iconLeft}
        {!noText && (
          <H3 as={Aria.Text} color="inherit">
            {title}
          </H3>
        )}
        {iconRight}
      </ButtonContentStyle>
    </ButtonStyle>
  )
}

export default Button
